import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SubPage, FullDivider } from "./SubPage";
import { OVERLAY_ONLY } from "../App";
import { getPricing, formatEuro, type Package, type AboTier, type KiPlan, type PricingData, type PricingLang } from "@/data/pricing";

const BRAND = "#FF3C00";

// `ki` used to be a boolean flag; it now carries the selected AI plan id.
// Legacy links with ki=1 / ki=true still resolve to the first plan.
const LEGACY_KI_TRUE = ["1", "true"];

function readQuery() {
  if (typeof window === "undefined") return { pkg: "", abo: "", ki: "" };
  const p = new URLSearchParams(window.location.search);
  return {
    pkg: p.get("package") ?? "",
    abo: p.get("abo") ?? "",
    ki: p.get("ki") ?? "",
  };
}

function writeQuery(state: { pkg?: string; abo: string; ki: string }) {
  if (typeof window === "undefined") return;
  const p = new URLSearchParams(window.location.search);
  if (state.pkg) p.set("package", state.pkg); else p.delete("package");
  if (state.abo) p.set("abo", state.abo); else p.delete("abo");
  if (state.ki) p.set("ki", state.ki); else p.delete("ki");
  const next = `${window.location.pathname}${p.toString() ? `?${p.toString()}` : ""}`;
  window.history.replaceState(null, "", next);
}

export function Pricing() {
  const { t, i18n } = useTranslation("pricing");
  const { t: tg } = useTranslation("overlayGate");
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const pricing = useMemo<PricingData>(() => getPricing(lang), [lang]);

  const initial = useMemo(() => readQuery(), []);
  const initialKiPlan = useMemo(() => {
    if (LEGACY_KI_TRUE.includes(initial.ki)) return pricing.kiPlans[0]?.id ?? "";
    return pricing.kiPlans.some((p) => p.id === initial.ki) ? initial.ki : "";
  }, [initial.ki, pricing]);
  const [aboId, setAboId] = useState<string>(initial.abo);
  const [kiPlanId, setKiPlanId] = useState<string>(initialKiPlan);
  const kiPlan = pricing.kiPlans.find((p) => p.id === kiPlanId) ?? null;

  useEffect(() => {
    writeQuery({ abo: aboId, ki: kiPlanId });
  }, [aboId, kiPlanId]);

  // In AI-overlay-only mode the site doesn't sell website building, so the
  // packages and maintenance contracts are dropped and only the AI layer is
  // priced. See the AI_OVERLAY_ONLY gate in App.tsx.
  return (
    <SubPage title={t("title")} subtitle={OVERLAY_ONLY ? tg("pricing.subtitle") : t("subtitle")}>
      {/* AI layer sits above the website pricing. */}
      <KiSection
        plans={pricing.kiPlans}
        lang={lang}
        selectedId={kiPlanId}
        onSelect={setKiPlanId}
        standalone={OVERLAY_ONLY}
      />

      {!OVERLAY_ONLY && (
        <>
          <FullDivider />

          <FilterBar
            tiers={pricing.aboTiers}
            aboId={aboId}
            onAbo={setAboId}
          />

          <FullDivider />

          <PackagesGrid
            pricing={pricing}
            aboId={aboId}
            kiPlan={kiPlan}
            lang={lang}
            initialHighlighted={initial.pkg}
          />
        </>
      )}

      <FullDivider />

      <FooterNote />
    </SubPage>
  );
}

function FilterBar({
  tiers, aboId, onAbo,
}: {
  tiers: AboTier[];
  aboId: string;
  onAbo: (id: string) => void;
}) {
  const { t } = useTranslation("pricing");
  return (
    <>
      <div className="md:hidden px-5 sm:px-8 py-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.08em] text-grey-500 shrink-0">{t("wartungLabel")}</span>
          <FilterChip active={aboId === ""} onClick={() => onAbo("")}>{t("wartungOhne")}</FilterChip>
          {tiers.map((t2) => (
            <FilterChip
              key={t2.id}
              active={aboId === t2.id}
              onClick={() => onAbo(t2.id)}
              suffix={`−${t2.discountPercent}%`}
            >
              {t2.eyebrow}
            </FilterChip>
          ))}
        </div>
      </div>

      <div className="hidden md:block">
        <div className="px-[66px] pt-8 pb-2 flex items-center gap-2">
          <span className="w-1.5 h-1.5" style={{ background: BRAND }} />
          <span className="text-[11px] font-mono uppercase tracking-[0.08em]" style={{ color: BRAND }}>{t("wartungEyebrow")}</span>
        </div>
        <div className="px-[66px] pb-5 text-[13px] text-grey-400 leading-snug max-w-2xl">{t("wartungLead")}</div>
        <div className="h-px bg-grey-800" />
        <div className="grid" style={{ gridTemplateColumns: `repeat(${tiers.length + 1}, minmax(0, 1fr))` }}>
          <FilterCard
            active={aboId === ""}
            onClick={() => onAbo("")}
            eyebrow={t("filterOhneEyebrow")}
            title={t("filterOhneTitle")}
            description={t("filterOhneDesc")}
            priceLine={null}
            badge="0%"
          />
          {tiers.map((t2) => (
            <FilterCard
              key={t2.id}
              active={aboId === t2.id}
              onClick={() => onAbo(t2.id)}
              eyebrow={t2.eyebrow}
              title={t2.name}
              description={t2.description}
              priceLine={{ price: t2.monthlyPrice, duration: t2.durationMonths }}
              badge={`−${t2.discountPercent}%`}
            />
          ))}
        </div>
        <div className="h-px bg-grey-800" />
      </div>
    </>
  );
}

function FilterCard({
  active, onClick, eyebrow, title, description, priceLine, badge,
}: {
  active: boolean;
  onClick: () => void;
  eyebrow: string;
  title: string;
  description: string;
  priceLine: { price: number; duration: number } | null;
  badge: string;
}) {
  const { t } = useTranslation("pricing");
  return (
    <button
      onClick={onClick}
      className="group flex flex-col text-left p-6 lg:p-7
                 border-l border-grey-800 first:border-l-0
                 transition-colors"
      style={{
        background: active ? "rgba(255,60,0,0.06)" : "transparent",
        outline: active ? `1px solid ${BRAND}` : "none",
        outlineOffset: -1,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "rgba(255,60,0,0.03)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-[10px] font-mono uppercase tracking-[0.08em] text-grey-500">{eyebrow}</span>
        <span
          className="text-base font-extrabold tracking-tight"
          style={{ color: active || priceLine ? BRAND : "#52525b" }}
        >
          {badge}
        </span>
      </div>

      <div className="text-lg font-bold text-white tracking-tight leading-tight mb-1">{title}</div>
      <div className="text-[12px] text-grey-400 leading-snug">{description}</div>

      <div className="mt-auto pt-5 flex items-end justify-between gap-2">
        <span
          className="text-[11px] font-semibold transition-colors"
          style={{ color: active ? BRAND : "#a1a1aa" }}
        >
          {active ? t("selected") : t("select")}
        </span>
        {priceLine && (
          <span className="text-[11px] text-grey-500 font-mono">
            €{priceLine.price}/Mo · {priceLine.duration} Mo.
          </span>
        )}
      </div>
    </button>
  );
}

function FilterChip({
  active, onClick, children, suffix,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  suffix?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium transition-colors leading-none"
      style={{
        background: active ? BRAND : "transparent",
        color: active ? "#fff" : "#d4d4d8",
        border: active ? `1px solid ${BRAND}` : "1px solid #27272a",
      }}
    >
      <span>{children}</span>
      {suffix && (
        <span className="opacity-70 text-[10px] font-mono">{suffix}</span>
      )}
    </button>
  );
}

function PackagesGrid({
  pricing, aboId, kiPlan, lang, initialHighlighted,
}: {
  pricing: PricingData;
  aboId: string;
  kiPlan: KiPlan | null;
  lang: PricingLang;
  initialHighlighted: string;
}) {
  const tier = aboId ? pricing.aboTiers.find((t2) => t2.id === aboId) ?? null : null;

  return (
    <>
      <div className="h-px bg-grey-800" />
      <div className="grid grid-cols-1 lg:grid-cols-3">
        {pricing.packages.map((pkg) => (
          <PackageCard
            key={pkg.id}
            pkg={pkg}
            tier={tier}
            kiPlan={kiPlan}
            lang={lang}
            highlightedFromUrl={initialHighlighted === pkg.id}
          />
        ))}
      </div>
      <div className="h-px bg-grey-800" />
    </>
  );
}

function PackageCard({
  pkg, tier, kiPlan, lang, highlightedFromUrl,
}: {
  pkg: Package;
  tier: AboTier | null;
  kiPlan: KiPlan | null;
  lang: PricingLang;
  highlightedFromUrl: boolean;
}) {
  const { t } = useTranslation("pricing");
  const isCustom = pkg.basePrice === null;
  const showHighlight = highlightedFromUrl || pkg.popular;
  const base = pkg.basePrice ?? 0;
  const discounted = tier && !isCustom ? Math.round(base * (1 - tier.discountPercent / 100)) : base;

  return (
    <a
      href={`/contact?package=${pkg.id}${tier ? `&abo=${tier.id}` : ""}${kiPlan ? `&ki=${kiPlan.id}` : ""}`}
      className="group flex flex-col p-6 lg:p-8
                 border-t border-grey-800 first:border-t-0
                 lg:border-t-0 lg:border-l lg:first:border-l-0 lg:border-grey-800
                 transition-colors hover:bg-[rgba(255,60,0,0.06)]"
      style={{ background: showHighlight ? "rgba(255,60,0,0.03)" : undefined }}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-mono text-grey-500">{pkg.number}</span>
        {showHighlight && (
          <span className="text-[11px] font-mono" style={{ color: BRAND }}>
            {highlightedFromUrl ? t("recommended") : t("popular")}
          </span>
        )}
      </div>

      <h3 className="text-2xl font-bold text-white tracking-tight mb-2 leading-tight">{pkg.name}</h3>
      <p className="text-[14px] text-grey-400 leading-snug mb-5">{pkg.desc}</p>

      {isCustom ? (
        <>
          <div className="text-2xl font-extrabold text-white">{t("onRequest")}</div>
          <p className="text-[11px] text-grey-500 mt-1 mb-6">{t("onRequestNote")}</p>
        </>
      ) : (
        <>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-3xl font-extrabold text-white">€{discounted}</span>
            {tier && (
              <span className="text-sm text-grey-500 line-through">€{base}</span>
            )}
          </div>
          <p className="text-[11px] text-grey-500 mt-1 mb-4">{t("onceTax")}</p>

          {(tier || kiPlan) && (
            <div className="text-[12px] text-grey-400 mb-5 leading-snug space-y-0.5">
              {tier && <div>{t("maintenance", { price: tier.monthlyPrice })}</div>}
              {kiPlan && (
                <div>{t("kiAdd", { price: formatEuro(kiPlan.monthlyPrice, lang), plan: kiPlan.name })}</div>
              )}
            </div>
          )}
        </>
      )}

      <ul className="flex flex-col gap-1.5 mb-6">
        {pkg.features.map((f, i) => {
          const isHeader = f.includes("plus:");
          if (isHeader) {
            return (
              <li key={i} className="text-[10px] font-mono uppercase tracking-[0.08em] text-grey-500 pb-1 mt-1 border-b border-grey-800">
                {f}
              </li>
            );
          }
          return (
            <li key={i} className="flex items-start gap-2 text-[13px] text-grey-300 leading-snug">
              <span className="font-bold mt-0.5 shrink-0 text-xs" style={{ color: BRAND }}>✓</span>
              <span>{f}</span>
            </li>
          );
        })}
      </ul>

      <span className="mt-auto inline-flex items-center gap-1.5 text-sm text-grey-300 group-hover:text-white transition-colors">
        {isCustom ? t("bookCall") : t("requestProject")} <span>→</span>
      </span>
    </a>
  );
}

function KiSection({
  plans, lang, selectedId, onSelect, standalone = false,
}: {
  plans: KiPlan[];
  lang: PricingLang;
  selectedId: string;
  onSelect: (id: string) => void;
  standalone?: boolean;
}) {
  const { t } = useTranslation("pricing");

  return (
    <div className="px-5 sm:px-8 md:px-[66px] py-8 md:py-10">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5" style={{ background: BRAND }} />
        <span className="text-[11px] font-mono uppercase tracking-[0.08em]" style={{ color: BRAND }}>{t("kiLabel")}</span>
      </div>
      <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight mb-1.5">{t("kiSectionTitle")}</h2>
      <p className="text-grey-400 max-w-2xl text-[13px] leading-snug mb-6">{t("kiSectionLead")}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map((plan) => (
          <KiPlanCard
            key={plan.id}
            plan={plan}
            lang={lang}
            // Overlay-only mode: the AI layer is the product, not an upsell —
            // the cards are informational, with no "add" toggle.
            selected={standalone ? true : selectedId === plan.id}
            standalone={standalone}
            onClick={() => onSelect(selectedId === plan.id ? "" : plan.id)}
          />
        ))}
      </div>
    </div>
  );
}

function KiPlanCard({
  plan, lang, selected, standalone, onClick,
}: {
  plan: KiPlan;
  lang: PricingLang;
  selected: boolean;
  standalone: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation("pricing");

  const body = (
    <>
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-sm font-bold text-white">{plan.name}</span>
        {plan.popular && (
          <span className="text-[10px] font-mono uppercase tracking-[0.08em]" style={{ color: BRAND }}>
            {t("popular")}
          </span>
        )}
      </div>
      <div className="flex items-baseline mb-3">
        <span className="text-2xl font-extrabold" style={{ color: BRAND }}>€{formatEuro(plan.monthlyPrice, lang)}</span>
        <span className="text-[11px] text-grey-500 ml-1">{t("kiMonthSuffix")}</span>
      </div>
      <ul className="flex flex-col gap-1">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-1.5 text-[12px] text-grey-300 leading-snug">
            <span className="text-grey-500 text-[10px] mt-1">●</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </>
  );

  const surface = {
    background: selected ? "rgba(255,60,0,0.04)" : "transparent",
    border: selected ? `1px solid ${BRAND}` : "1px solid #27272a",
  };

  if (standalone) {
    return <div className="flex flex-col p-4 md:p-5" style={surface}>{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col text-left p-4 md:p-5 transition-colors hover:bg-[rgba(255,60,0,0.04)]"
      style={surface}
      aria-pressed={selected}
    >
      {body}
      <span
        className="mt-4 text-xs font-semibold transition-colors"
        style={{ color: selected ? BRAND : "#d4d4d8" }}
      >
        {selected ? t("kiActive") : t("kiAddBtn")}
      </span>
    </button>
  );
}

function FooterNote() {
  const { t } = useTranslation("pricing");
  return (
    <div className="px-5 sm:px-8 md:px-[66px] py-12 md:py-16 text-center">
      <p className="text-sm text-grey-500 max-w-2xl mx-auto">
        {t("footerNote")}{" "}
        <a href="/contact" className="text-grey-300 hover:text-white underline">{t("footerNoteLink")}</a>
      </p>
    </div>
  );
}
